import { useCallback } from "react";
import { saveTemporaryWaypoint } from "../api";
import { useChangeDetection } from "./useChangeDetection";
import { SaveTemporaryWaypointParams } from "../types/requestInputs";

interface UseWaypointChangeDetectionParams {
    wptId: string;
    language: string | undefined;
    initialValue: any;
    onSaveSuccess?: () => void;
    onSaveError?: (error: any) => void;
}

/**
 * Specialized hook for waypoint change detection and saving
 * Checks for changes only when save is called
 */
export function useWaypointChangeDetection<T>({
    wptId,
    language,
    initialValue,
    onSaveSuccess,
    onSaveError,
}: UseWaypointChangeDetectionParams) {
    const { value, setValue, hasChanged, setOriginalValue } = useChangeDetection<T>(initialValue);

    const saveIfChanged = useCallback(
        async (
            saveParams: Partial<SaveTemporaryWaypointParams>,
            compareFn?: (current: T, original: T) => boolean,
        ): Promise<boolean> => {
            const token = localStorage.getItem("token");
            if (!token || !wptId || !language) {
                return false;
            }

            // Check if there are actual changes
            if (!hasChanged(compareFn)) {
                return false;
            }

            try {
                await saveTemporaryWaypoint({
                    token,
                    internalId: wptId,
                    language,
                    ...saveParams,
                });

                // Update original value after successful save
                setOriginalValue(value);
                onSaveSuccess?.();
                return true;
            } catch (error) {
                onSaveError?.(error);
                return false;
            }
        },
        [wptId, language, hasChanged, value, setOriginalValue, onSaveSuccess, onSaveError],
    );

    return {
        value,
        setValue,
        hasChanged,
        saveIfChanged,
        setOriginalValue,
    };
}
